<template>
  <div class="mt-6 ml-4 flex space-between space-x-4 justify-between m-2 mr-4 flex-end">
    <div>
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
        {{ t('rolesPage.title') }}
      </h1>
      <h1 class="text text-gray-900 dark:text-gray-200">
        {{ t('rolesPage.subtitle') }}
      </h1>
    </div>

    <div class="flex">
      <label class="input input-bordered flex items-center mr-2" style="width:330px">
        <input type="text" v-model="searchValue" class="grow" :placeholder="t('rolesPage.searchPlaceholder')" />
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
          <path fill-rule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clip-rule="evenodd" />
        </svg>
      </label>
      
      <button class="btn" @click="openRoleModal()" style="background-color:#e9e9e9;">{{ t('rolesPage.addNewRoleButton') }}</button>
    </div>
  </div>
  
  <div class="mt-6 px-4">
    <ThemeOnly theme="modern" :invert="true">
      <div
        class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex"
        style="border-radius: 10px; width:98%;height: 70vh;">
        <EasyDataTable :headers="headers" :items="roles" :loading="isLoading === true" buttons-pagination :search-field="searchFields"
    :search-value="searchValue">
      <template #item-rolename="{ rolename }">
        <div class="font-medium">
          {{ formatText(rolename) }}
        </div>
      </template>
      <template #item-permissions="{ permissions }">
        <div class="flex flex-wrap -m-1">
          <span v-for="permission in permissions" :key="permission" class="m-1 inline-flex items-center px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800 whitespace-nowrap h-6">
            {{ formatPermission(permission) }}
          </span>
        </div>
      </template>
      <template #item-options="{ roleid, rolename, permissions }">
        <button v-if="rolename !== 'Administrator' && rolename !== 'Agent Editor'" 
          @click="editRole({ roleid, rolename, permissions })" 
          class="mr-4 hover:text-blue-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('rolesPage.editButton') }}
        </button>
        <button v-if="rolename !== 'Administrator' && rolename !== 'Agent Editor'" 
          @click="openDeleteModal({ roleid, rolename, permissions })" 
          class="ml-2 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('rolesPage.deleteModalConfirmButton') }}
        </button>
        <span v-if="rolename === 'Administrator' || rolename === 'Agent Editor'" class="text-gray-500 text-sm">
          {{ t('rolesPage.systemRoleText') }}
        </span>
      </template>
      <template #loading> 
        <ModernLoader />
      </template>
    </EasyDataTable>
      </div>
    </ThemeOnly>
  </div>

  <it-modal v-model="isDeleteModalOpen">
    <div class="p-4">
      <h2 class="text-2xl font-bold mb-4">{{ t('rolesPage.confirmDeletionTitle') }}</h2>
      {{ t('rolesPage.confirmDeletionText') }}

      <div class="flex justify-end mt-6">
        <button @click="isDeleteModalOpen=false" type="button" class="mr-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          {{ t('roleModal.cancelButton') }}
        </button>
        <button @click="deleteRole()" type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
          {{ t('rolesPage.deleteModalConfirmButton') }}
        </button>
      </div>
    </div>
  </it-modal>

  <it-modal v-model="isRoleModalOpen">
    <div class="flex items-center justify-center px-4">
      <div class="p-1s max-w-2xl w-full" style="z-index:1000">
        <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {{ isEditingRole === true ? t('roleModal.editTitle') : t('roleModal.addTitle') }}
            </h3>
            <div class="flex items-center space-x-2">
              <span class="px-3 py-1 text-sm font-medium rounded-full" 
                :class="isEditingRole === true ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'">
                {{ isEditingRole === true ? t('roleModal.editMode') : t('roleModal.createMode') }}
              </span>
            </div>
          </div>
          
          <div class="space-y-6">
            <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ t('roleModal.roleNameLabel') }}
              </label>
              <input v-model="roleForm.roleName" 
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white" 
                type="text" 
                :placeholder="t('roleModal.roleNamePlaceholder')">
            </div>
            
            <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {{ t('roleModal.permissionsLabel') }}
              </label>
              <div class="max-h-60 overflow-y-auto space-y-2">
                <div v-for="permission in availablePermissions" :key="permission" 
                  class="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors">
                  <input type="checkbox" 
                    :id="permission"
                    v-model="roleForm.permissions"
                    :value="permission"
                    @change="handlePermissionChange(permission, $event)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                  <label :for="permission" 
                    class="ml-3 block text-sm text-gray-900 dark:text-gray-300">
                    {{ formatText(permission) }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-700">
          <button @click="isEditingRole === true ? updateRole() : saveRole()" 
            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            {{ isEditingRole === true ? t('roleModal.updateButton') : t('roleModal.createButton') }}
          </button>
          <button @click="closeRoleModal()" 
            class="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500">
            {{ t('roleModal.cancelButton') }}
          </button>
        </div>
      </div>
    </div>
  </it-modal>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import { useRouter } from 'vue-router';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import ModernLoader from '@/components/ModernLoader.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import { getLogger } from '@/shared/tools/logService.js';

const { t } = useI18n();
const toast = useToast();
const log = getLogger('Roles.vue');const isRoleModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const searchFields = ["rolename", "permissions"];
const searchValue = ref("");

const isEditingRole = ref(false);
const selectedRoleId = ref(null);
const isLoading = ref(false);
const availablePermissions = ref([
  'createUser',
  'modifyUser',
  'deleteUser',
  'createAiAgent',
  'modifyAiAgent',
  'deleteAiAgent',
  'viewAiAgents',
  'editAgents',
  'viewReports',
  'viewApiDocs',
  'viewInstallationWiki',
  'viewTools',
  'modifyTool',
  'createTool',
  'deleteTool',
  'accessSidebar',
  'Administator'
]);

const roleForm = reactive({
  roleName: '',
  permissions: []
});

const headers = [
  { text: "ID", value: "roleid", sortable: true },
  { text: "Role Name", value: "rolename", sortable: true },
  { text: "Permissions", value: "permissions" },
  { text: "Options", value: "options" }
];

const roles = ref([]);
const router = useRouter();

function getAuthToken() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function formatText(text) {
  if (text === null || text === undefined) {
    return '';
  }
  return text
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => {
      if (word.toLowerCase() === 'ai') {
        return 'AI';
      }
      
        
      
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

function formatPermission(permission) {
  return formatText(permission);
}

async function loadRoles() {
  try {
    isLoading.value = true;
    const response = await fetch(`${globalState.apiEndpoint}/api/roles/getAllRoles`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      console.log('Received roles data:', data);
      roles.value = data.map(role => {
        console.log('Mapping role:', role);
        return {
          roleid: isDefinedAndNotEmpty(role.roleId) != false ? role.roleId : role.roleid,
          rolename: isDefinedAndNotEmpty(role.roleName) != false ? role.roleName : role.rolename,
          permissions: isDefinedAndNotEmpty(role.permissions) != false ? role.permissions : []
        };
      });
      console.log('Mapped roles:', roles.value);
      isLoading.value = false;
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? t('rolesPage.toasts.loadErrorPrefix') + error.reason : t('rolesPage.toasts.loadError'));
      isLoading.value = false;
    }
  } catch (error) {
    toast.error('Error loading roles: ' + error.message);
    isLoading.value = false;
  }
}

async function saveRole() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/roles/createRole`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        roleName: roleForm.roleName,
        permissions: roleForm.permissions
      })
    });
    
    if (response.ok === true) {
      toast.success(t('rolesPage.toasts.createSuccess'));
      await loadRoles();
      closeRoleModal();
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? t('rolesPage.toasts.createErrorPrefix') + error.reason : t('rolesPage.toasts.createError'));
    }
  } catch (error) {
    toast.error('Error creating role: ' + error.message);
  }
}

async function updateRole() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/roles/updateRole/${selectedRoleId.value}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        roleName: roleForm.roleName,
        permissions: roleForm.permissions
      })
    });
    
    if (response.ok === true) {
      toast.success(t('rolesPage.toasts.updateSuccess'));
      await loadRoles();
      closeRoleModal();
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? t('rolesPage.toasts.updateErrorPrefix') + error.reason : t('rolesPage.toasts.updateError'));
    }
  } catch (error) {
    toast.error('Error updating role: ' + error.message);
  }
}

async function deleteRole() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/roles/deleteRole/${selectedRoleId.value}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    if (response.ok === true) {
      toast.success(t('rolesPage.toasts.deleteSuccess'));
      await loadRoles();
      isDeleteModalOpen.value = false;
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? t('rolesPage.toasts.deleteErrorPrefix') + error.reason : t('rolesPage.toasts.deleteError'));
    }
  } catch (error) {
    toast.error('Error deleting role: ' + error.message);
  }
}

function openRoleModal() {
  isEditingRole.value = false;
  roleForm.roleName = '';
  roleForm.permissions = [];
  isRoleModalOpen.value = true;
}

function closeRoleModal() {
  isRoleModalOpen.value = false;
  roleForm.roleName = '';
  roleForm.permissions = [];
}

function openDeleteModal(role) {
  console.log('Opening delete modal for role:', role);
  if (role === undefined || role === null) {
    toast.error(t('rolesPage.toasts.invalidRole'));
    return;
  }
  selectedRoleId.value = role.roleid;
  isDeleteModalOpen.value = true;
}

function editRole(role) {
  console.log('Editing role:', role);
  if (role === undefined || role === null) {
    toast.error(t('rolesPage.toasts.invalidRole'));
    return;
  }
  selectedRoleId.value = role.roleid;
  roleForm.roleName = role.rolename;
  roleForm.permissions = isDefinedAndNotEmpty(role.permissions) != false ? role.permissions : [];
  isEditingRole.value = true;
  isRoleModalOpen.value = true;
}

function handlePermissionChange(permission, event) {
  if (permission === 'Administator') {
    if (event.target.checked === true) {
      roleForm.permissions = [...availablePermissions.value];
    } else {
      roleForm.permissions = [];
    }
  } else {
    if (roleForm.permissions.includes('Administator') !== false && event.target.checked === false) {
      roleForm.permissions = roleForm.permissions.filter(p => p !== 'Administator');
    }
    if (roleForm.permissions.includes('Administator') === false && availablePermissions.value.length - 1 === roleForm.permissions.length) {
        if(availablePermissions.value.filter(p => p !== 'Administator').every(p => roleForm.permissions.includes(p)) !== false){
            roleForm.permissions.push('Administator');
        }
    }
  }
}

onMounted(() => {
  loadRoles();
});
</script>

<style>
table {
  font-family: "Heebo", sans-serif !important;
}
</style> 